import type { ButtonDesignTokens } from '@primeuix/themes/types/button'
import type { ButtonPassThroughOptions, ButtonProps } from 'primevue/button'
import type { RendererElement, RendererNode, VNode } from 'vue'

/**
 * Severity-палитра кнопок = роли из макета:
 *   primary  — жёлтая CTA #FFBF1A, текст #0F0F05 («Оформить заказ», «Отправить»)
 *   contrast — тёмная пилюля #0F0F05, текст белый («Смотреть», «Каталог»)
 *   secondary— светлая #F7F7F7, текст #0F0F05 (вторичные действия в шитах)
 *   danger   — красная #DA2128 («Очистить корзину», «Удалить адрес»)
 *
 * Цвета обязательно внутри colorScheme — иначе плоские root-токены
 * перебиваются значениями из Aura.
 */
const severities = {
  primary: {
    background: 'var(--accent-500)',
    hoverBackground: 'var(--accent-400)',
    activeBackground: 'var(--accent-600)',
    borderColor: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
    color: 'var(--accent-surface-color)',
    hoverColor: 'var(--accent-surface-color)',
    activeColor: 'var(--accent-surface-color)',
  },
  secondary: {
    background: 'var(--surface-muted)',
    hoverBackground: 'var(--secondary-100)',
    activeBackground: 'var(--secondary-200)',
    borderColor: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
    color: 'var(--text-color)',
    hoverColor: 'var(--text-color)',
    activeColor: 'var(--text-color)',
  },
  contrast: {
    background: 'var(--secondary-950)',
    hoverBackground: 'var(--secondary-800)',
    activeBackground: 'var(--black)',
    borderColor: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
    color: 'var(--white)',
    hoverColor: 'var(--white)',
    activeColor: 'var(--white)',
  },
  danger: {
    background: 'var(--primary-500)',
    hoverBackground: 'var(--primary-400)',
    activeBackground: 'var(--primary-600)',
    borderColor: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
    color: 'var(--white)',
    hoverColor: 'var(--white)',
    activeColor: 'var(--white)',
  },
  success: {
    background: 'var(--success-500)',
    hoverBackground: 'var(--success-500)',
    activeBackground: 'var(--success-500)',
    borderColor: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
    color: 'var(--white)',
    hoverColor: 'var(--white)',
    activeColor: 'var(--white)',
  },
  info: {
    background: 'var(--info-500)',
    hoverBackground: 'var(--info-500)',
    activeBackground: 'var(--info-500)',
    borderColor: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
    color: 'var(--white)',
    hoverColor: 'var(--white)',
    activeColor: 'var(--white)',
  },
} as const

const textVariants = {
  primary: {
    color: 'var(--text-color)',
    hoverBackground: 'var(--surface-muted)',
    activeBackground: 'var(--secondary-100)',
  },
  secondary: {
    color: 'var(--text-color-secondary)',
    hoverBackground: 'var(--surface-muted)',
    activeBackground: 'var(--secondary-100)',
  },
  contrast: {
    color: 'var(--text-color)',
    hoverBackground: 'var(--surface-muted)',
    activeBackground: 'var(--secondary-100)',
  },
  danger: {
    color: 'var(--primary-500)',
    hoverBackground: 'var(--primary-50)',
    activeBackground: 'var(--primary-100)',
  },
  info: {
    color: 'var(--link-color)',
    hoverBackground: 'var(--surface-muted)',
    activeBackground: 'var(--secondary-100)',
  },
} as const

const outlinedVariants = {
  primary: {
    borderColor: 'var(--accent-500)',
    color: 'var(--text-color)',
    hoverBackground: 'var(--accent-50)',
    activeBackground: 'var(--accent-100)',
  },
  secondary: {
    borderColor: 'var(--border-color)',
    color: 'var(--text-color)',
    hoverBackground: 'var(--surface-muted)',
    activeBackground: 'var(--secondary-100)',
  },
  contrast: {
    borderColor: 'var(--secondary-950)',
    color: 'var(--text-color)',
    hoverBackground: 'var(--surface-muted)',
    activeBackground: 'var(--secondary-100)',
  },
  danger: {
    borderColor: 'var(--primary-500)',
    color: 'var(--primary-500)',
    hoverBackground: 'var(--primary-50)',
    activeBackground: 'var(--primary-100)',
  },
} as const

/**
 * Геометрия из макета:
 *   обычная кнопка — 50px (14px/18 + padding 15), r=20
 *   small          — 32px (13px/16 + padding 7),  r=100 (пилюля)
 */
export const buttonConfig = (): ButtonDesignTokens => ({
  root: {
    borderRadius: 'var(--radius-xl)',
    roundedBorderRadius: 'var(--radius-pill)',
    gap: 'var(--space-4)',
    paddingX: 'var(--space-8)',
    paddingY: '15px',
    iconOnlyWidth: '50px',
    badgeSize: '18px',
    transitionDuration: '.15s',

    sm: { fontSize: '13px', paddingX: 'var(--space-6)', paddingY: '7px' },
    lg: { fontSize: '16px', paddingX: 'var(--space-9)', paddingY: '18px' },

    label: { fontWeight: 'var(--fw-bold)' },
    focusRing: { width: '0', style: 'none', offset: '0' },
  },

  colorScheme: {
    light: { root: severities, text: textVariants, outlined: outlinedVariants },
    dark: { root: severities, text: textVariants, outlined: outlinedVariants },
  },
})

/**
 * Размерный класс типографики и поддержка raw-SVG в prop `icon`
 * (иконки в проекте свои, primeicons не используем).
 */
export const buttonPt = (
  instance: VNode<RendererNode, RendererElement, ButtonProps>,
): ButtonPassThroughOptions => {
  const props = instance.props
  const icon = typeof props?.icon === 'string' ? props.icon : ''
  const isSvg = icon.includes('<svg')

  return {
    root: {
      class:
        props?.size === 'small' ? 'font-13-b' : props?.size === 'large' ? 'font-16-b' : 'font-14-b',
    },
    icon: isSvg
      ? {
          innerHTML: icon,
          class: `p-button-icon-svg p-button-icon-${props?.iconPos ?? 'left'}`,
        }
      : {},
  }
}
